/**
 * 面板仪表的模拟数据（真实接口只给到"区域概览"那九个字段，见
 * src/docs/external-area-overview-cockpit-api.md；驾驶舱改造后多出来的仪表
 * 暂时没有来源，先用这里补位，UI 上一律带「模拟」角标，别和真数据混看不分）。
 *
 * 两条口径：
 *   1) 同一栋楼每次算出来的值必须一样 —— 用楼号+楼名做种子，切走再切回来数字不跳；
 *   2) 只有跟着钟点走的那部分（今日累计、24h 曲线）允许随当前小时变化，
 *      这样面板看起来是活的，但又不会每帧重排。
 */

/** 批发服装市场的一天：早上开门前空场，上午和下午两个峰，17 点后收摊 */
const DAY_SHAPE = [
  0.004, 0.002, 0.002, 0.003, 0.006, 0.014, 0.03, 0.055, 0.085, 0.115, 0.132, 0.121, 0.082, 0.101, 0.118, 0.109,
  0.086, 0.055, 0.031, 0.019, 0.012, 0.008, 0.005, 0.003,
]

const fnv = (str) => {
  let h = 2166136261
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 16777619) >>> 0
  }
  return h
}

/** 线性同余：够用来做仪表数，不要密码学性质 */
function rand(seed) {
  let s = seed >>> 0
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0
    return s / 4294967296
  }
}

const between = (r, a, b) => a + r() * (b - a)
/** 街区级仪表共用的体量系数 */
const scale = (r) => 0.8 + r() * 0.5
const round = Math.round

/**
 * 一栋楼的仪表数据。字段全部是"看着像"的量级，不是统计结果。
 * @param {string} code 楼号
 * @param {string} name 楼名（进种子，同楼号换名也会换一套数）
 */
export function telemetryOf(code, name = '') {
  const r = rand(fnv(`${code}|${name}`))
  const scale = between(r, 0.55, 1.45)
  const now = new Date()
  const hour = now.getHours()

  // 24h 曲线：按市场日形打底，再给每栋楼一点自己的脾气（±18%）
  const shape = DAY_SHAPE.map((v) => Math.max(0, v * between(r, 0.82, 1.18)))
  const sum = shape.reduce((a, b) => a + b, 0) || 1
  const dayTotal = round(scale * between(r, 9000, 26000))
  const curve = shape.map((v) => round((v / sum) * dayTotal))
  const today = round(curve.slice(0, hour + 1).reduce((a, b) => a + b, 0))

  const parkingTotal = round(scale * between(r, 180, 620))
  const vacant = r() < 0.5 // 一半的楼留点空置，别全是满铺
  return {
    /** 今日累计客流（人） */
    flowToday: today,
    /** 客流同比（%），有正有负 */
    flowDelta: Math.round(between(r, -14, 26) * 10) / 10,
    /** 24 小时逐时客流 */
    flowCurve: curve,
    /** 当前小时在曲线里的位置，用来画"现在"这根针 */
    flowHour: hour,
    /** 开铺率（%）：已开门的铺位占比 */
    openRate: round(between(r, 76, 97)),
    /** 租金均价（元/㎡·天） */
    rentAvg: Math.round(between(r, 2.4, 8.2) * 10) / 10,
    /** 今日能耗（万 kWh） */
    energy: Math.round(scale * between(r, 0.9, 3.4) * 100) / 100,
    parking: { total: parkingTotal, free: round(parkingTotal * between(r, 0.05, 0.4)) },
    /** 设备在线率（%） */
    deviceOnline: Math.round(between(r, 91.5, 99.8) * 10) / 10,
    /** 未处理告警条数 + 分类，0 也要能画 */
    alarms: (() => {
      const total = Math.floor(between(r, 0, 6.4))
      const kinds = ['消防通道', '电梯困人', '监控离线', '烟感', '配电']
      return Array.from({ length: total }, () => ({
        kind: kinds[Math.floor(r() * kinds.length) % kinds.length],
        mins: 2 + Math.floor(r() * 168),
        level: r() < 0.22 ? 'high' : r() < 0.55 ? 'mid' : 'low',
      }))
    })(),
    /** 昨日客流（接口的 yesterdayNumber 为空时兜底） */
    yesterdayFlow: round(scale * between(r, 7000, 21000)),
    /** 接口没给经营品类时的构成：按楼型分三种脾气 */
    scopes: (() => {
      const kinds = [
        ['女装', '男装', '童装', '饰品'],
        ['女装', '面料', '辅料', '饰品'],
        ['男装', '鞋帽', '箱包', '女装'],
      ]
      const names = kinds[Math.floor(r() * kinds.length) % kinds.length]
      const shares = [0.52, 0.24, 0.15, 0.09].map((v) => v * between(r, 0.85, 1.15))
      const sum = shares.reduce((a, b) => a + b, 0)
      const total = round(between(r, 240, 620))
      return names.map((name, i) => ({ name, shopCount: Math.max(3, Math.round((shares[i] / sum) * total)) }))
    })(),
    /** 接口回 null 时用来兜底的三项（口径和区域概览字段一致） */
    fallback: {
      totalMerchants: round(scale * between(r, 260, 640)),
      operatingArea: round(scale * between(r, 1.6, 5.2) * 10000),
      practitioner: round(scale * between(r, 2200, 5200)),
      rentedCount: round(between(r, 280, 620)),
      vacantCount: vacant ? round(between(r, 12, 90)) : 0,
    },
  }
}

/**
 * 街区级仪表（大屏左右两栏那些没有来源的卡片）。
 * 种子用"今天"，所以同一天内刷新不会跳数，隔天换一套。
 * @param {Date} at
 */
export function districtTelemetry(at = new Date()) {
  const day = `${at.getFullYear()}-${at.getMonth() + 1}-${at.getDate()}`
  const r = rand(fnv(day))
  const hour = at.getHours()
  const levels = ['high', 'mid', 'low']
  const states = ['待处置', '处理中', '已完结']
  const kinds = [
    '东段人流密度超阈值',
    '配电房电流异常波动',
    '中庭烟感触发预警',
    '北门违停车辆占道',
    '货梯轿厢通讯中断',
    '消防通道被占用',
    '空调机组回风温度偏高',
  ]
  return {
    /** 天气：大屏顶栏那一排，接口没有 */
    weather: { temp: round(between(r, 12, 27)), text: ['晴', '多云', '阴', '小雨'][Math.floor(r() * 4) % 4] },
    /** 事件预警：条数 + 明细 */
    events: Array.from({ length: 4 }, (_, i) => ({
      text: kinds[(Math.floor(r() * kinds.length) + i * 3) % kinds.length],
      level: levels[Math.floor(r() * 3) % 3],
      state: i < 2 ? states[0] : i < 3 ? states[1] : states[2],
      mins: 6 + Math.floor(r() * 300),
    })),
    eventsDone: round(between(r, 150, 210)),
    /** 环境与能耗 */
    env: {
      temp: Math.round(between(r, 18, 27) * 10) / 10,
      humidity: round(between(r, 38, 74)),
      power: round(scale(r) * between(r, 1.2, 2.4) * 10000),
      water: round(between(r, 180, 520)),
    },
    /** 重点场馆运行：三条负载进度 */
    venues: [
      { name: '数字服装市集', metric: '客流承载', pct: round(between(r, 58, 96)) },
      { name: '潮流发布中心', metric: '场地使用', pct: round(between(r, 34, 82)) },
      { name: '智慧停车楼', metric: '车位占用', pct: round(between(r, 62, 97)) },
    ],
    /** 系统运行状态 */
    system: {
      online: Math.round(between(r, 98.2, 99.95) * 100) / 100,
      iot: round(between(r, 1100, 1500)),
      video: round(between(r, 760, 980)),
      edge: round(between(r, 38, 74)),
    },
    /** 分时客流（大屏柱图，12 档 = 每 2 小时） */
    hours: Array.from({ length: 12 }, (_, i) => {
      const h = i * 2
      const w = DAY_SHAPE[h] + DAY_SHAPE[Math.min(23, h + 1)]
      return { label: `${String(h).padStart(2, '0')}:00`, value: Math.round(w * scale(r) * between(r, 26000, 42000)) }
    }),
    /** 当前小时落在哪根柱子上（高亮那根） */
    peakIndex: Math.min(11, Math.max(0, Math.floor(hour / 2))),
  }
}

/**
 * 接口字段 -> 展示值。null/空串时退回模拟值并标 sim，UI 据此打角标。
 * @param {number|string|null|undefined} real 接口原值
 * @param {number} sim 模拟兜底
 */
export function pickNum(real, sim) {
  const v = Number(real)
  return real === null || real === undefined || real === '' || !Number.isFinite(v)
    ? { value: sim, sim: true }
    : { value: v, sim: false }
}
